import type { ConversationType } from '@repo/types/chat';

/**
 * Props for useChatName hook.
 * @property name - The chat name or a JSON string mapping user IDs to names.
 * @property type - The type of conversation ('individual', 'group', etc.).
 * @property userId - The user ID for individual chats (optional).
 */
export interface ChatNameProps {
  name?: string;
  type?: ConversationType;
  userId?: number;
}

/**
 * Returns the display name for a chat based on its type and user ID.
 * For 'individual' chats, attempts to parse the name as a JSON object and
 * retrieve the name for the given userId. For other types, returns the name as is.
 *
 * @param chatNameProps - The properties for determining the chat name.
 * @returns The resolved chat name, or undefined if not found.
 */
export function useChatName(chatNameProps: ChatNameProps): string | undefined {
  const { type, name, userId } = chatNameProps;

  const getObjectKeyByValue = (obj: Record<string, string>, targetKey: number | undefined) => {
    const entry = Object.entries(obj).find(([key]) => String(key) === String(targetKey));
    return entry ? entry[1] : undefined;
  };

  if (!type || !name) return undefined;

  if (type === 'individual') {
    try {
      return getObjectKeyByValue(JSON.parse(name), userId);
    } catch (error) {
      console.error('Error parsing chat name:', error);
      return name;
    }
  }
  return name;
}
